/**
 * Session cache for the dev-mode banner status (TES-1660, Layout-Stabilität).
 *
 * Caching the status per session lets the banner's first render already show the
 * final state — no async pop-in that shoves the TopAppBar + content down ~48px.
 * The first page load of a session still fetches; every follow-up navigation
 * renders stable immediately. Kept in its own module so DevModeBanner.tsx stays a
 * component-only file (react-refresh) and these pure helpers can be unit-tested.
 */

export interface DevModeStatus {
  isProductionMode: boolean;
  hasResendKey: boolean;
  missingSecrets: string[];
  hasAnyAuthUsers?: boolean | null;
}

// Module-private: only the helpers below read/write it. Kept un-exported so it
// is not flagged as an unused export (fallow dead-code) — the test guards the
// stable key value via the literal string instead.
const DEV_MODE_STATUS_CACHE_KEY = "devModeBannerStatus";

export function readCachedStatus(): DevModeStatus | null {
  try {
    const raw = sessionStorage.getItem(DEV_MODE_STATUS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as DevModeStatus) : null;
  } catch {
    return null;
  }
}

export function writeCachedStatus(status: DevModeStatus): void {
  try {
    sessionStorage.setItem(DEV_MODE_STATUS_CACHE_KEY, JSON.stringify(status));
  } catch {
    // sessionStorage unavailable (private mode / quota) — degrade to per-mount fetch.
  }
}
